import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, AlertCircle, Clock, ChevronRight, Package, ListTodo } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProjectStatusReport() {
  const [report, setReport] = useState(null);
  const [pageAudits, setPageAudits] = useState([]);
  const [burndown, setBurndown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('priorities');
  const [showAddReport, setShowAddReport] = useState(false);
  const [reportText, setReportText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [manualPriority, setManualPriority] = useState('auto');

  const getAuthToken = () => localStorage.getItem('gio_token') || localStorage.getItem('token') || localStorage.getItem('memberToken');

  useEffect(() => {
    fetchProjectStatus();
    fetchPageAudits();
    fetchBurndown();
  }, []);

  const fetchBurndown = async () => {
    try {
      const token = localStorage.getItem('gio_token') || localStorage.getItem('token') || localStorage.getItem('memberToken');
      if (!token) return;
      const response = await fetch(`${API_URL}/api/productivity/audit-burndown?weeks=12`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBurndown(data.series || []);
      }
    } catch (error) {
      console.error('Error fetching burndown:', error);
    }
  };

  const toggleAuditNote = async (noteId, newStatus) => {
    try {
      const token = localStorage.getItem('gio_token') || localStorage.getItem('token') || localStorage.getItem('memberToken');
      const response = await fetch(`${API_URL}/api/page-audit/note/${noteId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        fetchPageAudits();
        fetchProjectStatus();
        fetchBurndown();
      } else {
        alert('Failed to update audit note');
      }
    } catch (e) {
      console.error('Error updating audit note:', e);
    }
  };

  const fetchProjectStatus = async () => {
    try {
      // Check both admin and member token keys
      const token = localStorage.getItem('gio_token') || localStorage.getItem('token') || localStorage.getItem('memberToken');
      if (!token) {
        console.error('No auth token found in localStorage');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/api/productivity/project-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        console.error('API error:', response.status, response.statusText);
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      setReport(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching project status:', error);
      setLoading(false);
    }
  };

  const fetchPageAudits = async () => {
    try {
      const token = localStorage.getItem('gio_token') || localStorage.getItem('token') || localStorage.getItem('memberToken');
      if (!token) return; // Silent fail for page audits
      
      const response = await fetch(`${API_URL}/api/page-audit/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPageAudits(data.audits || []);
      }
    } catch (error) {
      console.error('Error fetching page audits:', error);
    }
  };

  const exportReport = () => {
    const exportData = {
      generated_at: new Date().toISOString(),
      health: report?.health,
      page_audits: pageAudits,
      in_progress: report?.in_progress,
      pending_issues: report?.pending_issues,
      upcoming: report?.upcoming,
      completed: report?.completed
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `project-status-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const submitReport = async () => {
    if (!reportText.trim()) return;
    
    setSubmitting(true);
    try {
      const token = getAuthToken();
      if (!token) {
        alert('⚠️ Authentication required. Please log in again.');
        setSubmitting(false);
        return;
      }
      
      const lines = reportText.split('\n').map(l => l.trim()).filter(Boolean);
      const useBatch = batchMode && lines.length > 1;
      
      const singleBody = { report_text: reportText };
      if (manualPriority !== 'auto') singleBody.priority = manualPriority;
      const response = await fetch(`${API_URL}/api/productivity/${useBatch ? 'add-report-bulk' : 'add-report'}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(useBatch ? { report_texts: lines } : singleBody)
      });
      
      if (response.ok) {
        const result = await response.json();
        if (useBatch) {
          alert(`✅ ${result.added} reports added successfully!${result.ai_categorized ? '\n\nAI categorized each item with priority and effort.' : '\n\n(AI unavailable — items added as P2 pending issues, you can edit them.)'}`);
        } else {
          alert(`✅ Report added successfully!\n\nCategory: ${result.category}\nPriority: ${result.priority}\nEffort: ${result.estimated_effort}`);
        }
        setReportText('');
        setManualPriority('auto');
        setShowAddReport(false);
        // Refresh project status
        fetchProjectStatus();
        fetchBurndown();
      } else {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        const detail = typeof error.detail === 'string' ? error.detail : JSON.stringify(error.detail);
        alert(`Failed to add report: ${detail || 'Please try again.'}`);
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Error submitting report. Check console for details.');
    } finally {
      setSubmitting(false);
    }
  };

  const markReportDone = async (reportId) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/productivity/reports/${reportId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'DONE' })
      });
      if (response.ok) fetchProjectStatus();
      else alert('Failed to mark report as done');
      fetchBurndown();
    } catch (e) {
      console.error('Error marking report done:', e);
    }
  };

  const deleteReport = async (reportId) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/productivity/reports/${reportId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) fetchProjectStatus();
      else alert('Failed to delete report');
      fetchBurndown();
    } catch (e) {
      console.error('Error deleting report:', e);
    }
  };

  const changeReportPriority = async (reportId, priority) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}/api/productivity/reports/${reportId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ priority })
      });
      if (response.ok) { fetchProjectStatus(); fetchBurndown(); }
      else alert('Failed to update priority');
    } catch (e) {
      console.error('Error updating priority:', e);
    }
  };

  const LiveReportControls = ({ item }) => {
    if (item.source !== 'live') return null;
    return (
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <span className="text-[11px] px-2 py-0.5 rounded bg-pink-500/20 text-pink-300 border border-pink-500/30">USER REPORT</span>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-400">Priority:</span>
          <select
            data-testid={`live-report-priority-${item.id}`}
            value={item.priority || 'P2'}
            onChange={(e) => changeReportPriority(item.id, e.target.value)}
            className="text-xs px-2 py-1 rounded-lg bg-gray-800 text-white border border-white/20 focus:border-pink-500 outline-none cursor-pointer"
          >
            <option value="P0">P0 · Critical</option>
            <option value="P1">P1 · High</option>
            <option value="P2">P2 · Normal</option>
          </select>
        </div>
        <button
          data-testid={`live-report-done-${item.id}`}
          onClick={() => markReportDone(item.id)}
          className="text-xs px-3 py-1 rounded-lg bg-green-600/20 text-green-400 border border-green-500/30 hover:bg-green-600/40 transition-all"
        >
          ✓ Mark Done
        </button>
        <button
          data-testid={`live-report-delete-${item.id}`}
          onClick={() => deleteReport(item.id)}
          className="text-xs px-3 py-1 rounded-lg bg-red-600/20 text-red-400 border border-red-500/30 hover:bg-red-600/40 transition-all"
        >
          ✕ Delete
        </button>
      </div>
    );
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      'P0': 'bg-red-500/20 text-red-400 border-red-500/30',
      'P1': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'P2': 'bg-amber-500/20 text-amber-400 border-amber-500/30'
    };
    return colors[priority] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-800 rounded w-1/3"></div>
          <div className="h-32 bg-gray-800 rounded"></div>
          <div className="h-64 bg-gray-800 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Project Status Report</h1>
              <p className="text-gray-400">Comprehensive overview of all tasks, issues, priorities, and page audits</p>
            </div>
            <div className="flex gap-3">
              <button
                data-testid="add-report-btn"
                onClick={() => setShowAddReport(true)}
                className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-semibold inline-flex items-center gap-2 transition-all"
              >
                <ListTodo size={16} />
                Add Report
              </button>
              <button
                onClick={exportReport}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-semibold inline-flex items-center gap-2 transition-all"
              >
                <Package size={16} />
                Export Report
              </button>
            </div>
          </div>
          {!report && (
            <div className="mt-4 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
              <p className="text-red-400">⚠️ No data loaded. Check console for errors.</p>
            </div>
          )}
        </div>

        {/* Health Summary */}
        {report?.health && (
          <>
            {report.pre_deployment && (
              <div className="mb-8 rounded-2xl border border-amber-500/30 bg-amber-900/10 p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="text-sm text-amber-300 mb-1">Pre-deployment status</div>
                    <h2 className="text-xl font-semibold text-white">{report.pre_deployment.overall_status}</h2>
                    <p className="text-xs text-gray-500 mt-1">Local HEAD: {report.pre_deployment.head}</p>
                  </div>
                  <span className="text-xs text-amber-300 border border-amber-500/30 rounded px-2 py-1">DEPLOYMENT PENDING</span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
                  <div>
                    <h3 className="font-semibold text-white mb-2">Completed local fixes</h3>
                    <ul className="space-y-1 text-gray-400 list-disc list-inside">
                      {report.pre_deployment.completed_local_fixes.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Deployment blockers</h3>
                    <ul className="space-y-1 text-gray-400 list-disc list-inside">
                      {report.pre_deployment.deployment_blockers.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Production verification</h3>
                    <ul className="space-y-1 text-gray-400 list-disc list-inside">
                      {report.pre_deployment.production_verification.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-2xl p-6">
                <div className="text-sm text-gray-400 mb-2">Overall Status</div>
                <div className="text-2xl font-bold text-white">{report.health.status}</div>
                <div data-testid="project-status-score" className="text-xs text-gray-400 mt-2" title={report.health.label_definition || ''}>
                  {report.health.health_score == null ? 'Feature score: not measured' : `Feature score: ${report.health.health_score}%`}
                  {' · '}{report.health.features_untested ?? 0} untested · {report.health.features_broken ?? 0} broken of {report.health.features_total ?? 0}
                </div>
              </div>
              <div className="bg-gray-900/50 border border-white/10 rounded-2xl p-6">
                <div className="text-sm text-gray-400 mb-2">Completion</div>
                <div className="text-2xl font-bold text-white">{report.health.completion_percentage}%</div>
              </div>
              <div className="bg-gray-900/50 border border-white/10 rounded-2xl p-6">
                <div className="text-sm text-gray-400 mb-2">Completed</div>
                <div className="text-2xl font-bold text-green-400">{report.health.total_completed}</div>
              </div>
              <div className="bg-gray-900/50 border border-white/10 rounded-2xl p-6">
                <div className="text-sm text-gray-400 mb-2">In Progress</div>
                <div className="text-2xl font-bold text-amber-400">{report.health.total_in_progress}</div>
              </div>
            </div>
            
            {/* Last 7 Days Activity (live data) */}
            {report?.health?.last_7_days && (
              <div data-testid="last-7-days-summary" className="mb-8 p-6 rounded-2xl border-2 border-pink-500/40 bg-gradient-to-r from-purple-900/30 to-pink-900/30">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-pink-400" />
                  Last 7 Days Activity
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Completed</div>
                    <div className="text-3xl font-bold text-green-400">{report.health.last_7_days.completed}</div>
                    <div className="text-xs text-gray-500 mt-1">Reports + audit notes marked done</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Added</div>
                    <div className="text-3xl font-bold text-purple-400">{report.health.last_7_days.added}</div>
                    <div className="text-xs text-gray-500 mt-1">New items reported this week</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Open</div>
                    <div className="text-3xl font-bold text-orange-400">{report.health.last_7_days.open}</div>
                    <div className="text-xs text-gray-500 mt-1">Total items still pending</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Success Rate</div>
                    <div className="text-3xl font-bold text-pink-400">{report.health.last_7_days.success_rate}</div>
                    <div className="text-xs text-gray-500 mt-1">Done vs still open</div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Audit Burn-down Chart */}
        {burndown.length > 0 && burndown.some(b => b.total > 0) && (
          <div data-testid="audit-burndown-chart" className="mb-8 p-6 bg-gray-900/50 border border-white/10 rounded-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <ChevronRight className="w-5 h-5 text-pink-400" />
                Audit Burn-down · last 12 weeks
              </h3>
              <span className="text-xs text-gray-400">
                Open: <span className="text-pink-400 font-bold">{burndown[burndown.length - 1]?.open ?? 0}</span>
                {' · '}Done: <span className="text-green-400 font-bold">{burndown[burndown.length - 1]?.done ?? 0}</span>
              </span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={burndown} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="bdOpen" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f472b6" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#f472b6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="bdDone" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4ade80" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#4ade80" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="week" stroke="#71717a" tick={{ fontSize: 11 }} />
                <YAxis stroke="#71717a" tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#18181b', border: '1px solid rgba(255,16,240,0.3)', borderRadius: '8px' }} labelStyle={{ color: '#fff' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="open" name="Open" stroke="#f472b6" strokeWidth={2} fill="url(#bdOpen)" />
                <Area type="monotone" dataKey="done" name="Done" stroke="#4ade80" strokeWidth={2} fill="url(#bdDone)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 border-b border-white/10 overflow-x-auto">
          {['priorities', 'page-audits', 'completed', 'pending', 'in-progress', 'upcoming', 'backlog'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-medium capitalize transition-all duration-200 border-b-2 whitespace-nowrap ${
                activeTab === tab
                  ? 'text-purple-400 border-purple-500'
                  : 'text-gray-400 border-transparent hover:text-gray-300'
              }`}
            >
              {tab.replace('-', ' ')}
              {tab === 'page-audits' && pageAudits.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded-full text-xs">
                  {pageAudits.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="space-y-4">
          {/* Page Audits Tab */}
          {activeTab === 'page-audits' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-pink-400" />
                <span>Page Audit Reports ({pageAudits.length})</span>
              </h2>
              {pageAudits.length === 0 ? (
                <div className="text-center py-12 bg-gray-900/50 border border-white/10 rounded-xl">
                  <p className="text-gray-400">No page audits found</p>
                </div>
              ) : (
                pageAudits.map((audit, idx) => {
                  const pendingNotes = audit.notes?.filter(n => n.status === 'pending') || [];
                  const completedNotes = audit.notes?.filter(n => n.status === 'done') || [];
                  
                  return (
                    <div key={idx} className="bg-gray-900/50 border border-white/10 rounded-xl p-6 hover:border-pink-500/30 transition-all">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-1">{audit.page}</h3>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded ${
                              audit.status === 'ok' ? 'bg-green-500/20 text-green-400' :
                              audit.status === 'needs-work' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {audit.status?.toUpperCase()}
                            </span>
                            {pendingNotes.length > 0 && (
                              <span className="text-xs px-2 py-1 rounded bg-orange-500/20 text-orange-400">
                                {pendingNotes.length} Pending
                              </span>
                            )}
                            {completedNotes.length > 0 && (
                              <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-400">
                                {completedNotes.length} Done
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {audit.notes && audit.notes.length > 0 && (
                        <div className="space-y-2">
                          {audit.notes.map((note, nIdx) => (
                            <div key={note.id || nIdx} className={`p-3 rounded-lg border ${
                              note.status === 'done' 
                                ? 'bg-green-900/10 border-green-500/30' 
                                : 'bg-gray-800/50 border-gray-700'
                            }`}>
                              <div className="flex items-start justify-between gap-2">
                                <p className={`text-sm flex-1 ${note.status === 'done' ? 'text-gray-500 line-through' : 'text-gray-300'}`}>
                                  {note.text}
                                </p>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    note.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                                    note.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-gray-500/20 text-gray-400'
                                  }`}>
                                    {note.priority}
                                  </span>
                                  {note.status === 'done' ? (
                                    <button
                                      data-testid={`audit-note-reopen-${note.id}`}
                                      onClick={() => toggleAuditNote(note.id, 'pending')}
                                      className="text-xs px-2 py-0.5 rounded bg-gray-600/30 text-gray-300 border border-gray-500/30 hover:bg-gray-600/50 transition-all"
                                    >
                                      ↩ Reopen
                                    </button>
                                  ) : (
                                    <button
                                      data-testid={`audit-note-done-${note.id}`}
                                      onClick={() => toggleAuditNote(note.id, 'done')}
                                      className="text-xs px-2 py-0.5 rounded bg-green-600/20 text-green-400 border border-green-500/30 hover:bg-green-600/40 transition-all"
                                    >
                                      ✓ Done
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
          
          {/* Next Priorities */}
          {activeTab === 'priorities' && report?.in_progress && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                <ListTodo className="w-5 h-5 text-purple-400" />
                <span>Next Priorities</span>
              </h2>
              {report.in_progress.map((task, idx) => (
                <div key={idx} className="bg-gray-900/50 border border-white/10 rounded-xl p-6 hover:border-purple-500/30 transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className={`px-3 py-1 rounded-lg text-xs font-semibold border ${getPriorityBadge(task.priority)}`}>
                        {task.priority}
                      </div>
                      <h3 className="text-lg font-semibold text-white">{task.title}</h3>
                    </div>
                    {task.progress && (
                      <span className="text-sm text-purple-400 font-medium">{task.progress}</span>
                    )}
                  </div>
                  <p className="text-gray-400 mb-3">{task.description}</p>
                  {task.blocked_on && (
                    <div className="inline-flex items-center space-x-2 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>Blocked: {task.blocked_on}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Completed Tasks */}
          {activeTab === 'completed' && report?.completed && (
            <div className="space-y-6">
              {report.completed.map((phase, idx) => (
                <div key={idx} className="bg-gray-900/50 border border-white/10 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-white">{phase.phase}</h3>
                    <span className="text-sm text-gray-400">{phase.completed_date}</span>
                  </div>
                  <div className="space-y-2">
                    {phase.tasks.map((task, taskIdx) => (
                      <div key={taskIdx} className="flex items-center space-x-3 text-gray-300">
                        <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                        <span>{task}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pending Issues */}
          {activeTab === 'pending' && report?.pending_issues && (
            <div className="space-y-4">
              {report.pending_issues.map((issue, idx) => (
                <div key={issue.id || idx} data-testid={`pending-issue-${idx}`} className="bg-gray-900/50 border border-orange-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className={`px-3 py-1 rounded-lg text-xs font-semibold border ${getPriorityBadge(issue.priority)}`}>
                        {issue.priority}
                      </div>
                      <h3 className="text-lg font-semibold text-white">{issue.title}</h3>
                    </div>
                    <span className="text-sm text-gray-400">{issue.estimated_effort}</span>
                  </div>
                  <p className="text-gray-400">{issue.description}</p>
                  {(issue.type || issue.confidence || issue.estimate || issue.source) && (
                    <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-400">
                      {issue.type && <span className="rounded border border-white/10 px-2 py-1">Type: {issue.type}</span>}
                      {issue.confidence && <span className="rounded border border-white/10 px-2 py-1">Confidence: {issue.confidence}</span>}
                      {issue.estimate && <span className="rounded border border-white/10 px-2 py-1">{issue.estimate}</span>}
                      {issue.source && <span className="rounded border border-white/10 px-2 py-1">Source: {issue.source}</span>}
                    </div>
                  )}
                  <LiveReportControls item={issue} />
                </div>
              ))}
            </div>
          )}

          {/* In Progress */}
          {activeTab === 'in-progress' && report?.in_progress && (
            <div className="space-y-4">
              {report.in_progress.map((task, idx) => (
                <div key={idx} className="bg-gray-900/50 border border-amber-500/30 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <Clock className="w-5 h-5 text-amber-400" />
                      <h3 className="text-lg font-semibold text-white">{task.title}</h3>
                    </div>
                    {task.progress && (
                      <span className="text-sm text-amber-400 font-medium">{task.progress}</span>
                    )}
                  </div>
                  <p className="text-gray-400 mb-3">{task.description}</p>
                  {task.blocked_on && (
                    <div className="inline-flex items-center space-x-2 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>Blocked: {task.blocked_on}</span>
                    </div>
                  )}
                  <LiveReportControls item={task} />
                </div>
              ))}
            </div>
          )}

          {/* Upcoming */}
          {activeTab === 'upcoming' && report?.upcoming && (
            <div className="space-y-4">
              {report.upcoming.map((task, idx) => (
                <div key={idx} className="bg-gray-900/50 border border-white/10 rounded-xl p-6 hover:border-purple-500/20 transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className={`px-3 py-1 rounded-lg text-xs font-semibold border ${getPriorityBadge(task.priority)}`}>
                        {task.priority}
                      </div>
                      <h3 className="text-lg font-semibold text-white">{task.title}</h3>
                    </div>
                    <span className="text-sm text-gray-400">{task.estimated_effort}</span>
                  </div>
                  <p className="text-gray-400">{task.description}</p>
                  <LiveReportControls item={task} />
                </div>
              ))}
            </div>
          )}

          {/* Backlog */}
          {activeTab === 'backlog' && report?.backlog && (
            <div className="bg-gray-900/50 border border-white/10 rounded-xl p-6">
              <div className="space-y-3">
                {report.backlog.map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-3 text-gray-300 p-3 hover:bg-white/5 rounded-lg transition-all">
                    <Circle className="w-4 h-4 text-gray-500" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Report Modal */}
      {showAddReport && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAddReport(false)}>
          <div className="bg-gray-900 border-2 border-pink-500/40 rounded-2xl p-6 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">Add New Report</h2>
              <button onClick={() => setShowAddReport(false)} className="text-gray-400 hover:text-white">
                ✕
              </button>
            </div>
            
            <p className="text-gray-400 text-sm mb-4">
              Describe the task, issue, or feature. AI will automatically categorize it with priority, effort estimate, and add it to the appropriate section.
            </p>
            
            <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="batch-mode-checkbox"
                checked={batchMode}
                onChange={(e) => setBatchMode(e.target.checked)}
                className="w-4 h-4 accent-pink-500"
              />
              <span className="text-sm text-gray-300">Batch mode — one item per line (great for pasting audit lists)</span>
            </label>
            
            <textarea
              data-testid="report-textarea"
              value={reportText}
              onChange={(e) => setReportText(e.target.value)}
              placeholder={batchMode
                ? "Paste one audit item per line, e.g.:\nFix the broken avatar on landing page\nAdd cookie consent banner\nPictures gallery needs folder structure..."
                : "Example: The notification bell is not showing new messages for members. Need to add separate notification feeds for admin vs regular members..."}
              className="w-full h-40 px-4 py-3 rounded-xl text-white placeholder-gray-500 resize-none"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,16,240,0.3)"
              }}
            />
            
            {!batchMode && (
              <div className="mt-4">
                <label className="block text-sm text-gray-300 mb-2">Priority</label>
                <div className="flex gap-2 flex-wrap" data-testid="manual-priority-selector">
                  {[
                    { v: 'auto', label: '✨ Auto (AI decides)' },
                    { v: 'P0', label: 'P0 · Critical' },
                    { v: 'P1', label: 'P1 · High' },
                    { v: 'P2', label: 'P2 · Normal' },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`priority-option-${opt.v}`}
                      onClick={() => setManualPriority(opt.v)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                        manualPriority === opt.v
                          ? 'bg-pink-500/30 text-pink-200 border-pink-500'
                          : 'bg-gray-800 text-gray-400 border-white/10 hover:border-pink-500/40'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-500 mt-1">
                  {manualPriority === 'auto'
                    ? 'AI will categorize the priority automatically.'
                    : `You are manually setting this report to ${manualPriority} — AI will not override it.`}
                </p>
              </div>
            )}
            
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowAddReport(false)}
                className="px-6 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-white transition-all"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                data-testid="submit-report-btn"
                onClick={submitReport}
                disabled={submitting || !reportText.trim()}
                className="px-6 py-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Processing...' : batchMode ? '✨ Submit Batch & AI Categorize' : '✨ Submit & AI Categorize'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
